import * as types from 'constants/ActionTypes';

const initState = {
  accountSummary: {},
  billingInformation: {},
  initParameters: null,
  orderPreferences: {},
  isRoofUser: false,
  userProducts: {},
  productCatalog: {}, // #genai broader catalog (external + internal) for filters
  controlStates: {},
  companyInformation: {},
  orgDescendants: {},
  entitledApps: [],
  entitledThirdPartyApps: null, // #genai
  subscriptionDashboard: {},
  consumption: {},
  userProfile: {},
  loading: {
    initParameters: false,
    billingInformation: false,
    userProducts: false,
    productCatalog: false, // #genai
    orderPreferences: false,
    controlStates: false,
    companyInformation: false,
    orgDescendants: false,
    accountSummary: false,
    entitledApps: false,
    entitledThirdPartyApps: false, // #genai
    subscriptionDashboard: false,
    consumption: false,
    userProfile: false,
  },
  error: {
    initParameters: null,
    billingInformation: null,
    userProducts: false,
    productCatalog: false, // #genai
    orderPreferences: null,
    controlStates: null,
    companyInformation: false,
    orgDescendants: false,
    accountSummary: false,
    entitledApps: false,
    entitledThirdPartyApps: false, // #genai
    subscriptionDashboard: false,
    consumption: false,
    userProfile: false,
  },
};

// #genai - Map uniform request states outside the reducer switch to keep it focused on payload updates.
const loadingActionKeys = {
  [types.INIT_PARAMETERS_LOADING]: 'initParameters',
  [types.GET_BILLING_INFORMATION_LOADING]: 'billingInformation',
  [types.ORDER_PREFERENCES_LOADING]: 'orderPreferences',
  [types.CONTROL_STATES_LOADING]: 'controlStates',
  [types.USER_PRODUCTS_LOADING]: 'userProducts',
  [types.PRODUCT_CATALOG_LOADING]: 'productCatalog',
  [types.COMPANY_INFO_LOADING]: 'companyInformation',
  [types.ORG_DESCENDANTS_LOADING]: 'orgDescendants',
  [types.LOADING_ACCOUNT_SUMMARY]: 'accountSummary',
  [types.USER_ENTITLED_APPS_LOADING]: 'entitledApps',
  [types.ENTITLED_THIRDPARTY_APPS_LOADING]: 'entitledThirdPartyApps',
  [types.LOADING_SUBSCRIPTION_DASHBOARD]: 'subscriptionDashboard',
  [types.USER_PROFILE_LOADING]: 'userProfile',
  [types.LOAD_CONSUMPTION_DATA_LOADING]: 'consumption',
};

const errorActionKeys = {
  [types.INIT_PARAMETERS_ERROR]: 'initParameters',
  [types.GET_BILLING_INFORMATION_ERROR]: 'billingInformation',
  [types.ORDER_PREFERENCES_ERROR]: 'orderPreferences',
  [types.CONTROL_STATES_ERROR]: 'controlStates',
  [types.USER_PRODUCTS_ERROR]: 'userProducts',
  [types.PRODUCT_CATALOG_ERROR]: 'productCatalog',
  [types.COMPANY_INFO_ERROR]: 'companyInformation',
  [types.ORG_DESCENDANTS_ERROR]: 'orgDescendants',
  [types.ACCOUNT_SUMMARY_ERROR]: 'accountSummary',
  [types.USER_ENTITLED_APPS_ERROR]: 'entitledApps',
  [types.ENTITLED_THIRDPARTY_APPS_ERROR]: 'entitledThirdPartyApps',
  [types.SUBSCRIPTION_DASHBOARD_ERROR]: 'subscriptionDashboard',
  [types.USER_PROFILE_ERROR]: 'userProfile',
  [types.LOAD_CONSUMPTION_DATA_ERROR]: 'consumption',
};

export default (state = initState, action = {}) => {
  const loadingKey = loadingActionKeys[action.type];
  if (loadingKey) {
    return {
      ...state,
      loading: { ...state.loading, [loadingKey]: true },
      error: { ...state.error, [loadingKey]: false },
    };
  }

  const errorKey = errorActionKeys[action.type];
  if (errorKey) {
    return {
      ...state,
      loading: { ...state.loading, [errorKey]: false },
      error: { ...state.error, [errorKey]: true },
    };
  }

  switch (action.type) {
    case types.PLACE_ORDER_SUCCESS: return { ...state, orderPreferences: {} };
    case types.INIT_PARAMETERS_SUCCESS: return {
      ...state,
      initParameters: action.initParameters,
      loading: { ...state.loading, initParameters: false },
      error: { ...state.error, initParameters: false },
    };
    case types.GET_BILLING_INFORMATION_SUCCESS: return {
      ...state,
      billingInformation: action.billingInformation,
      loading: { ...state.loading, billingInformation: false },
      error: { ...state.error, billingInformation: false },
    };
    // #genai Phase 2 deep-link fix (Add-ACH / Add-Card): merge only billPayerId (fetched from
    // /bank-account-info) onto billingInformation without disturbing the rest of the
    // /bill-payer-info payload. Save path (buildBillingEffect) already reads
    // billingInformationSnapshot?.billPayerId as a fallback, so this makes the field available
    // without pushing accountSuffix/isACHDefault into billingInformation.achInfo (which would
    // flip AchPaymentInformation into summary mode and clobber updateAchDefault(true)).
    case types.SET_USER_DATA_BILL_PAYER_ID: return {
      ...state,
      billingInformation: {
        ...state.billingInformation,
        billPayerId: action.billPayerId,
      },
    };
    case types.RESET_BILLING_INFO: return {
      ...state,
      billingInformation: initState.billingInformation,
    };
    case types.USER_PRODUCTS_SUCCESS: return {
      ...state, isRoofUser: action.isRoofUser, userProducts: action.userProducts, loading: { ...state.loading, userProducts: false },
    };
    // #genai catalog for OH/Analytics filters; does not touch userProducts or isRoofUser
    case types.PRODUCT_CATALOG_SUCCESS: return {
      ...state,
      productCatalog: action.productCatalog,
      loading: { ...state.loading, productCatalog: false },
      error: { ...state.error, productCatalog: false },
    };
    case types.ORDER_PREFERENCES_SUCCESS: return {
      ...state,
      orderPreferences: action.orderPreferencesResponse,
      loading: { ...state.loading, orderPreferences: false },
      error: { ...state.error, orderPreferences: false },
    };
    case types.CONTROL_STATES_SUCCESS: return {
      ...state,
      controlStates: action.controlStatesResponse,
      loading: { ...state.loading, controlStates: false },
    };
    case types.COMPANY_INFO_SUCCESS: return {
      ...state,
      companyInformation: action.companyInformation,
      loading: { ...state.loading, companyInformation: false },
    };
    case types.ORG_DESCENDANTS_SUCCESS: return {
      ...state,
      orgDescendants: action.orgDescendants,
      loading: { ...state.loading, orgDescendants: false },
    };
    case types.ACCOUNT_SUMMARY_SUCCESS: return {
      ...state,
      accountSummary: action.accountSummary,
      loading: { ...state.loading, accountSummary: false },
      error: { ...state.error, accountSummary: false },
    };

    case types.USER_ENTITLED_APPS_SUCCESS: return {
      ...state,
      entitledApps: action.entitledApps,
      loading: { ...state.loading, entitledApps: false },
      error: { ...state.error, entitledApps: false },
    };
    case types.ENTITLED_THIRDPARTY_APPS_SUCCESS: return { // #genai
      ...state,
      entitledThirdPartyApps: action.entitledThirdPartyApps,
      loading: { ...state.loading, entitledThirdPartyApps: false },
      error: { ...state.error, entitledThirdPartyApps: false },
    };
    case types.SUBSCRIPTION_DASHBOARD_SUCCESS: return {
      ...state,
      subscriptionDashboard: action.subscriptionDashboard,
      loading: { ...state.loading, subscriptionDashboard: false },
      error: { ...state.error, subscriptionDashboard: false },
    };
    case types.USER_PROFILE_SUCCESS: return {
      ...state,
      userProfile: action.userProfile,
      loading: { ...state.loading, userProfile: false },
      error: { ...state.error, userProfile: false },
    };
    case types.LOAD_CONSUMPTION_DATA_SUCCESS: return {
      ...state,
      consumption: action.data || {},
      loading: { ...state.loading, consumption: false },
      error: { ...state.error, consumption: false },
    };

    default: return state;
  }
};
